import { useContext } from "react";
import { useStoreState } from "pullstate";
import globalState from "src/globalState";
import UserContext from "src/contexts/UserContext";
import { useMfwThemeProvider } from "myfitworld-utils";
import { useShouldShowSwitchMFWOriginals } from "./useShouldShowMfwOriginals";
import { ContentFilterOptions } from "src/utils/contentFilterOptions";
import { useShouldShowOrgOriginals } from "./useShouldShowOrgOriginals";
import { useShouldShowPremium } from "./useShouldShowPremium";
import { useShouldShowPublic } from "./useShouldShowPublic";
import { useShouldShowLockedContent } from "./useShouldShowLockedContent";
import useAdminTrainerCountsAndRoles from "src/hooks/useAdminTrainerCountsAndRoles";
import { useShouldShowMyNet } from "./useShouldShowMyNet";

export interface VisibilitySwitches {
  showMFWOriginalsSwitch: boolean,
  showOrgOriginalsSwitch: boolean,
  showMyNetSwitch: boolean,
  showLockedContentSwitch: boolean,
  showPremiumSwitch: boolean,
  showPublicSwitch: boolean,
  isWLNetPremium:boolean,
}
export const useShouldShowSwitches = (options: ContentFilterOptions, entityName: string): VisibilitySwitches => {
  // Universal:
  const mfwTheme = useMfwThemeProvider();
  const { user } = useContext(UserContext);

  // Org settings:
  const currentOrganization = useStoreState(globalState, s => s.currentOrganization);
  const organizationId = currentOrganization?.id ?? "";

  const {sumAdminsTrainers: totalAdminTrainerCount, isSuperAdmin :isSuperAdminUnd, isClient : isClientUnd} = useAdminTrainerCountsAndRoles(organizationId, user);
  const isSuperAdmin : boolean = isSuperAdminUnd ?? false;
  const isClient: boolean = isClientUnd ?? false;

  const isMfwPlatform = mfwTheme.theme.orgId === process.env.REACT_APP_MFW_ORGANIZATION_ID;
  const isAnotherOrgOnMFWPlatform = isMfwPlatform && organizationId !== process.env.REACT_APP_MFW_ORGANIZATION_ID;
  const isMfwOnMfwPlatform:boolean = !isAnotherOrgOnMFWPlatform && isMfwPlatform;
  const isWhiteLabel: boolean = mfwTheme.theme.isWhiteLabel;
  
  const hasMfwContent: boolean = mfwTheme.theme.whitelabel.haveMFWContent;

  // Content settings:
  const isNutrition: boolean = entityName === "Grocery" || entityName === "Meal" || entityName === "NutritionPlan";
  const isWLNetPremium : boolean = (organizationId === 'cDnEg7nAapSox6x29Bq6' || organizationId === 'Iz7l16DMRvilzBrrU52E' || organizationId === 'jorUBhWI60BWhOOH91M8') ? true : false; // Iam, kinera && b8m

  const showMFWOriginalsSwitch = useShouldShowSwitchMFWOriginals({
    option: options.showMFWOriginals ?? false,
    isClient,
    isSuperAdmin,
    isAnotherOrgOnMFWPlatform,
    isWhiteLabel,
    hasMfwContent,
  });
  const showOrgOriginalsSwitch = useShouldShowOrgOriginals({
    totalAdminTrainerCount,
    isMfwOnMfwPlatform,
    isClient,
    option: options.showORGOriginals ?? false
  });
  const showPremiumSwitch = useShouldShowPremium({
    option: options.showPremium ?? false,
    isMfwOnMfwPlatform, 
    isSuperAdmin,
    currentOrganization,
  })
  // Net:
  const showPublicSwitch = useShouldShowPublic({
    option: options?.showPublic !== false,
    isNutrition,
  })
  // MyNet:
  const showMyNetSwitch = useShouldShowMyNet({
    option: options.showMyMfwNet ?? false,
    isNutrition,
  })

  const showLockedContentSwitch = useShouldShowLockedContent({
    option: options.isLockedContentDisplayed ?? false,
    isNutrition,
    isClient
  });

  return {
    showMFWOriginalsSwitch,
    showOrgOriginalsSwitch,
    showMyNetSwitch,
    showPremiumSwitch,
    showLockedContentSwitch,
    showPublicSwitch,
    isWLNetPremium,
  };
};